/**
 * Diff ligne à ligne par plus longue sous-séquence commune.
 *
 * Les documents font quelques dizaines de lignes : la table quadratique est
 * largement suffisante et évite d'ajouter une dépendance pour ça.
 *
 * @param {string} oldText
 * @param {string} newText
 * @returns {Array<{type: 'same'|'added'|'removed', text: string}>}
 */
export const diffLines = (oldText, newText) => {
  const a = (oldText || '').split('\n');
  const b = (newText || '').split('\n');
  const lcs = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));

  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }

  const rows = [];
  let i = 0;
  let j = 0;

  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      rows.push({ type: 'same', text: a[i] });
      i++; j++;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      rows.push({ type: 'removed', text: a[i] });
      i++;
    } else {
      rows.push({ type: 'added', text: b[j] });
      j++;
    }
  }
  while (i < a.length) rows.push({ type: 'removed', text: a[i++] });
  while (j < b.length) rows.push({ type: 'added', text: b[j++] });

  return rows;
};

/**
 * Replie les longues plages inchangées d'un diff.
 *
 * Une mise en forme ne change souvent que quelques lignes sur cinquante :
 * afficher les cinquante noie le changement. Les plages de « same » qui
 * dépassent leur contexte (`avant + apres`) d'au moins 2 lignes sont
 * remplacées par un marqueur `{type: 'fold', count}` qui dit combien de
 * lignes sont masquées. Une seule ligne cachée ne vaut pas le marqueur :
 * il prend la même hauteur qu'une ligne de code et retire l'information
 * affichée sans rien gagner en place.
 *
 * @param {Array<{type: string, text: string}>} rows sortie de diffLines
 * @param {number|null} contextLines lignes gardées de chaque côté ; null = pas de repli
 * @returns {Array<{type: 'same'|'added'|'removed'|'fold', text?: string, count?: number}>}
 */
export const foldUnchanged = (rows, contextLines = 3) => {
  if (contextLines === null || contextLines === undefined) return rows;

  const result = [];
  let i = 0;

  while (i < rows.length) {
    if (rows[i].type !== 'same') {
      result.push(rows[i]);
      i++;
      continue;
    }

    let fin = i;
    while (fin < rows.length && rows[fin].type === 'same') fin++;

    const plage = rows.slice(i, fin);
    // Une plage en tête ou en queue n'a de contexte que d'un côté.
    const avant = i === 0 ? 0 : contextLines;
    const apres = fin === rows.length ? 0 : contextLines;

    // Seuil +1 : une plage qui ne depasse son contexte que d'une ligne ne
    // doit pas etre repliee — le marqueur prendrait la meme hauteur que la
    // ligne qu'il cacherait, sans rien faire gagner.
    if (plage.length > avant + apres + 1) {
      result.push(...plage.slice(0, avant));
      result.push({ type: 'fold', count: plage.length - avant - apres });
      if (apres > 0) result.push(...plage.slice(plage.length - apres));
    } else {
      result.push(...plage);
    }

    i = fin;
  }

  return result;
};

export default diffLines;
